


















































import { computed, defineComponent, onUnmounted, ref } from "@vue/composition-api";
import {
  FundraisingGoal,
  FundraisingGoalReward,
  FundraisingGoalRewardStatus,
  FundraisingGoalStatus,
} from "@scrile/api-provider/dist/api/FundraisingGoalsProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useFiles from "@/hooks/useFiles";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { Tab } from "@/types";
import { EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BasePage from "@/components/BasePage";
import BaseTabs from "@/components/BaseTabs";
import EmptyContent from "@/components/EmptyContent";
import CreateRewardModal from "@/components/FundraisingGoal/CreateRewardModal";
import DeleteRewardConfirm from "@/components/FundraisingGoal/DeleteRewardConfirm";
import FundraisingGoalCard from "@/components/FundraisingGoal/FundraisingGoalCard";
import FundraisingGoalRewardCard from "@/components/FundraisingGoal/FundraisingGoalRewardCard";
import { InputRewardCreate } from "@/components/FundraisingGoal/types";
import PageTitle from "@/components/PageTitle";
import FundraisingGoalDonations from "./components/FundraisingGoalDonations.vue";

const statusPriority: Record<FundraisingGoalRewardStatus, number> = {
  [FundraisingGoalRewardStatus.NEW]: 0,
  [FundraisingGoalRewardStatus.ACTIVE]: 1,
  [FundraisingGoalRewardStatus.DECLINED]: 2,
  [FundraisingGoalRewardStatus.DELETED]: 3,
};

export default defineComponent({
  name: "PageFundraisingGoalDetails",
  components: {
    CreateRewardModal,
    DeleteRewardConfirm,
    BaseButton,
    FundraisingGoalCard,
    FundraisingGoalRewardCard,
    FundraisingGoalDonations,
    PageTitle,
    EmptyContent,
    BaseTabs,
    BasePage,
  },
  props: {
    goalId: { type: String, required: true },
  },
  setup(props) {
    const { authUserStrict, authorities } = useAuthUser();
    const { uploadThenUpdate } = useFiles();
    const loading = ref(false);
    const processing = ref(false);
    const rewardIdToDelete = ref<string | null>(null);
    const activeTab = ref("info");
    const goal = ref<FundraisingGoal | null>(null);
    const rewards = ref<FundraisingGoalReward[]>([]);
    const showCreateReward = ref(false);

    const canUpdateGoal = computed(() => checkAuthorities(authorities.value, ["updateMyFundraisingGoal"]));
    const canAddReward = computed(
      () =>
        canUpdateGoal.value &&
        goal.value &&
        [FundraisingGoalStatus.ACTIVE, FundraisingGoalStatus.NEW].includes(goal.value.status)
    );
    const sortedRewards = computed(() =>
      rewards.value.sort((a, b) => {
        const statusDiff = statusPriority[a.status] - statusPriority[b.status];
        if (statusDiff !== 0) return statusDiff;
        return a.amount - b.amount;
      })
    );

    const tabs: Tab[] = [
      { text: $t("Info"), value: "info" },
      { text: $t("Donations"), value: "donations" },
    ];

    const updateGoal = (data?: FundraisingGoal) => {
      if (!data) return;
      goal.value = data;
    };

    const getGoalInfo = async () => {
      const response = await providers.FundraisingGoalsProvider.search({
        data: { userId: authUserStrict.value.id, ids: [props.goalId] },
      });

      goal.value = response.result.find(i => i.id === props.goalId) ?? null;
    };

    const getRewards = async () => {
      const response = await providers.FundraisingGoalsProvider.rewardSearch({
        data: { goalId: props.goalId, size: 2000 },
      });
      rewards.value = response.result;
    };

    const getData = async () => {
      try {
        loading.value = true;
        await Promise.all([getGoalInfo(), getRewards()]);
      } finally {
        loading.value = false;
      }
    };

    const onRewardSubmit = async (data: InputRewardCreate) => {
      try {
        processing.value = true;
        const [newReward] = await providers.FundraisingGoalsProvider.rewardCreate({
          data: [{ ...data.data, goalId: props.goalId }],
        });
        if (data.file) {
          newReward.file = await uploadThenUpdate({
            file: data.file,
            fileId: { subjectId: newReward.id, type: "fundraising_goal_reward" },
          });
        }
        rewards.value.push(newReward);
        showCreateReward.value = false;
      } finally {
        processing.value = false;
      }
    };

    const onRewardDelete = async () => {
      if (!rewardIdToDelete.value) return;
      try {
        processing.value = true;
        const response = await providers.FundraisingGoalsProvider.rewardDelete(rewardIdToDelete.value);
        rewards.value = rewards.value.map(i => (i.id === response.id ? response : i));
        rewardIdToDelete.value = null;
      } finally {
        processing.value = false;
      }
    };

    getData();

    emitter.on<FundraisingGoal>(EVENTS.FUNDRAISING_GOAL_UPDATED, updateGoal);
    onUnmounted(() => {
      emitter.off<FundraisingGoal>(EVENTS.FUNDRAISING_GOAL_UPDATED, updateGoal);
    });

    return {
      loading,
      processing,
      activeTab,
      tabs,
      goal,
      sortedRewards,
      canUpdateGoal,
      canAddReward,
      rewardIdToDelete,
      showCreateReward,
      config,
      FundraisingGoalStatus,
      onRewardSubmit,
      onRewardDelete,
    };
  },
});
